import React, { useState } from 'react';
import { SiteModel } from '../../../models/DataAdminTypes';
import { useCaseModalLogic } from './useCaseModalLogic';
import { TriageStep } from './steps/TriageStep';
import { DetailsFormStep } from './steps/DetailsFormStep';
import { FileSelectModal } from '../FileSelectModal';

/**
 * Props for the NewCaseFolderModal component.
 */
export interface NewCaseFolderModalProps {
  /** Function to retrieve the current access token. */
  getAccessToken: () => Promise<string>;
  /** The current environment string (e.g., 'dev', 'prod'). */
  env: string;
  /** Callback function triggered when the modal is closed. Passing a folder string implies success. */
  onClose: (folder: string | null) => void;
  /** The site model associated with the case creation. */
  site: SiteModel;
}

/**
 * A modal component that orchestrates the creation of a new case folder.
 * * It implements a multi-step wizard:
 * 1. Triage Step: Determines if the case is Staged or links to a previous stage.
 * 2. Details Step: Collects case metadata (ID, Date, Procedures).
 * 3. File Upload Step: Automatically transitions to the FileSelectModal upon successful folder creation.
 * * @param props - {@link NewCaseFolderModalProps}
 */
export const NewCaseFolderModal: React.FC<NewCaseFolderModalProps> = props => {
  const { state, actions, refs } = useCaseModalLogic(props);
  const [showUploadModal, setShowUploadModal] = useState(false);

  /**
   * Manually dispatches a submit event to the form element.
   * This allows the "Next" button in the modal footer (outside the form) to trigger validation and submission.
   */
  const triggerSubmit = () => {
    if (refs.formRef.current) {
      refs.formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  };

  /**
   * Effect to handle the transition from Case Creation to File Upload.
   * When the folder is successfully created, we switch the view state instead of closing.
   */
  React.useEffect(() => {
    if (state.isCreated && state.newFolderName) {
      setShowUploadModal(true);
    }
  }, [state.isCreated, state.newFolderName]);

  // If the folder is created and we are in upload mode, render the FileSelectModal.
  if (showUploadModal && state.newFolderName) {
    return (
      <FileSelectModal
        getAccessToken={props.getAccessToken}
        env={props.env}
        siteCode={props.site.siteCode}
        caseFolder={state.newFolderName}
        subFolder="" 
        onClose={() => {
          // Return the new folder name to the parent so the UI can refresh/navigate.
          actions.onClose(state.newFolderName);
        }}
      />
    );
  }

  // Render the Case Creation Wizard (Step 1 or Step 2)
  return (
    <div className="modal-background">
      {/* Loading Overlay */}
      {state.submitting && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
            <p className="text-gray-700 font-medium">Creating case folder...</p>
          </div>
        </div>
      )}

      <div className="modal-content w-8/10 h-9/10 max-w-9/10 max-h-9/10">
        <div data-testid="modal-subcontent" className="modal-subcontent flex-1 flex flex-col w-8/10 max-w-9/10 max-h-9/10">

          <div className="modal-header">
            <h2 className="modal-header-text">Add Case Details</h2>
            <button className="modal-close-button" onClick={() => actions.onClose(null)} aria-label="Close modal">
              <span className="text-2xl text-gray-500 hover:text-gray-700">×</span>
            </button>
          </div>

          <div className="text-left pl-6">
            Please specify the case ID number, the case date, and case details.
          </div>

          {state.errorMessage && (
            <div data-testid="error-message" className="bg-red-100 text-red-700 p-4 m-6 rounded">
              {state.errorMessage}
            </div>
          )}

          <div id="form-container" className="flex-1 flex items-start justify-center overflow-auto pl-6 pt-8">
            <div className="w-full h-full">
              {state.step === 1 && <TriageStep state={state} actions={actions} />}

              {state.step === 2 && (
                <DetailsFormStep
                  state={state}
                  actions={actions}
                  formRef={refs.formRef as React.RefObject<HTMLFormElement>}
                />
              )}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="modal-actions">
          <>
            {/* Left Button: Cancel (Step 1) OR Back (Step 2) */}
            {state.step === 1 ? (
              <button onClick={() => actions.onClose(null)} className='action-secondary'>Cancel</button>
            ) : (
              <button onClick={actions.handleBack} className='action-secondary'>Back</button>
            )}

            {/* Right Button: Next (Step 1) OR Submit (Step 2) */}
            {state.step === 1 ? (
              <button
                onClick={actions.handleNext}
                className={`${state.isTriageValid ? 'action-primary' : 'action-disabled'}`}
                disabled={!state.isTriageValid}
              >
                Next
              </button>
            ) : (
              <button
                onClick={triggerSubmit}
                data-testid="submit-button"
                className={`${state.isFormValid ? 'action-primary' : 'action-disabled'}`}
                disabled={state.submitting || !state.isFormValid}
              >
                {state.submitting ? 'Creating...' : 'Next'}
              </button>
            )}
          </>
        </div>
      </div>
    </div>
  );
};
